import { useApolloClient, useMutation } from "@apollo/client";
import ObjectID from "bson-objectid";
import {
  CreateMessageMutation,
  CreateMessageMutationVariables,
  CreateMessageThreadMutation,
  CreateMessageThreadMutationVariables,
  MessageFileInput,
  MessageFragment,
  MessageInput,
  MessageThreadFragment,
  MessageThreadInput,
  MessageThreadType,
  QuotedMessageFragment,
  TaskInput,
} from "gql/graphql";
import CREATE_MESSAGE_MUTATION from "modules/messaging/_networking/create-message";
import CREATE_MESSAGE_THREAD_MUTATION from "modules/messaging/_networking/create-message-thread";
import MESSAGE_THREAD_FRAGMENT from "modules/messaging/_networking/message-thread-fragment";
import { useAuth } from "modules/providers/auth-provider";
import {
  IFileInTransit,
  IMessage,
  IMessageThreadDraft,
} from "modules/providers/index-db-provider/index-db-models";
import {
  MIXPANEL_EVENT_TYPE,
  PERSON_COUNT_TYPE,
  useMixpanel,
} from "modules/providers/mixpanel-provider/mixpanel-provider";
import { usePubSub } from "modules/providers/pub-sub-provider";
import { useUsers } from "modules/providers/users-provider";
import moment from "moment";
import { useRouter } from "next/router";
import { useCallback, useMemo } from "react";
import { CHATS_PATH, HOME_PATH, THREADS_PATH } from "modules/utils/Constants";
import {
  getMessageFileInput,
  getMessageThreadTypeName,
  isImage,
  isUserActiveAndSmsOnly,
  isVideo,
} from "modules/utils/Functions";
import useMessageThreadCache from "./use-message-thread-cache";
import { MESSAGE_THREAD_ID_QUERY_STRING } from "./use-query-string";

const useCreateMessage = (messageThreadId?: string) => {
  const { me } = useAuth();
  const { cache } = useApolloClient();
  const { isEmailUser, getUsersById } = useUsers();
  const { track, incrementPersonProperty } = useMixpanel();
  const { getOptimisticMessage } = useMessageThreadCache();
  const { pathname, push, query } = useRouter();
  const { publish } = usePubSub();

  const getImageCount = useCallback(
    (files?: IFileInTransit[]) =>
      files
        ? files.reduce(
            (sum, obj) => (isImage(obj.contentType) ? sum + 1 : sum),
            0,
          )
        : 0,
    [],
  );

  const getVideoCount = useCallback(
    (files?: IFileInTransit[]) =>
      files
        ? files.reduce(
            (sum, obj) => (isVideo(obj.contentType) ? sum + 1 : sum),
            0,
          )
        : 0,
    [],
  );

  const getQuickReplyMessageInput = useCallback(
    (text: string, quotedMessageId: string): MessageInput => ({
      id: new ObjectID().toHexString(),
      text,
      quillDelta: "\n",
      quotedMessageList: [
        {
          id: new ObjectID().toHexString(),
          messageId: quotedMessageId,
        },
      ],
      gifList: [],
      fileList: [],
      calloutUserIdList: [],
      distributionGroupIdList: [],
      tagIdList: [],
      sentTime: moment().toISOString(),
    }),
    [],
  );

  const [createMessageThreadAPI, { loading: isCreateMessageThreadLoading }] =
    useMutation<
      CreateMessageThreadMutation,
      CreateMessageThreadMutationVariables
    >(CREATE_MESSAGE_THREAD_MUTATION);

  const getTaskInput = useCallback(
    (draft: IMessageThreadDraft): TaskInput | undefined => {
      if (draft.type !== MessageThreadType.Task || !draft.task) {
        return undefined;
      }
      return {
        descriptionMessageId: draft.messageId,
        assignedUserId: draft.task.assignedUserId,
        dueTime: draft.task.dueTime,
        statusType: draft.task.statusType,
        priorityType: draft.task.priorityType,
      };
    },
    [],
  );

  const getMessageThreadInputFromDraft = useCallback(
    (draft: IMessageThreadDraft): MessageThreadInput => ({
      id: draft.messageThreadId,
      sentTime: moment().toISOString(),
      subject: draft.subject,
      type: draft.type,
      isLocked: draft.isLocked,
      createMessageUserIdList: draft.createMessageUserIdList,
      task: getTaskInput(draft),
      happycoWorkOrderId: draft.happycoWorkOrderId,
    }),
    [getTaskInput],
  );

  const getMessageInputFromDraft = useCallback(
    (draft: IMessage, files?: IFileInTransit[]): MessageInput => ({
      id: draft.messageId,
      sentTime: moment().toISOString(),
      text: draft.text,
      quillDelta: draft.delta,
      quotedMessageList: draft.quotedMessageList?.map((m) => ({
        id: new ObjectID().toHexString(),
        messageId: m.messageId,
        file:
          m.file &&
          ({
            id: m.file.id,
            name: m.file.name,
            contentType: m.file.contentType,
            key: m.file.key,
            previewKey: m.file.previewKey,
            isCompressed: !!m.file.isCompressed,
            isVoiceNote: m.file.isVoiceNote,
          } as MessageFileInput),
      })),
      relatedMessageThreadIdList:
        draft.relatedMessageThread &&
        draft.relatedMessageThread.isRemoved === false
          ? [draft.relatedMessageThread.id]
          : [],
      gifList: draft.gifList,
      fileList: files?.map(
        (file): MessageFileInput => getMessageFileInput(file),
      ),
      calloutUserIdList: draft.calloutUserIdList,
      distributionGroupIdList: draft.distributionGroupIdList,
      tagIdList: draft.tagIdList,
    }),
    [],
  );

  const smsUserCount = useCallback(
    (messageThread: MessageThreadFragment) =>
      getUsersById(messageThread.accessUserIdList).filter((user) =>
        isUserActiveAndSmsOnly(user),
      ).length,
    [getUsersById],
  );

  const hasSmsUsers = useCallback(
    (messageThread: MessageThreadFragment) => smsUserCount(messageThread) > 0,
    [smsUserCount],
  );

  const createMessageThread = useCallback(
    async (
      messageThreadInput: MessageThreadInput,
      messageInput: MessageInput,
      files?: IFileInTransit[],
    ) => {
      const { data } = await createMessageThreadAPI({
        variables: {
          input: messageThreadInput,
          messageInput,
        },
      });
      const messageThread = data?.createMessageThread;
      if (!messageThread?.id) {
        throw new Error("Unable to parse message thread id.");
      }
      track(MIXPANEL_EVENT_TYPE.MESSAGE_SENT, {
        "New Conversation": true,
        "Conversation Type": getMessageThreadTypeName(messageThread),
        "Linked Conversation":
          (messageInput.relatedMessageThreadIdList?.length || 0) > 0,
        "Count of Hashtags": messageInput.tagIdList.length,
        "Count of Dist Groups": messageInput.distributionGroupIdList.length,
        "Count of Callouts": messageInput.calloutUserIdList.length,
        "Count of Emails": 0,
        "Count of Photos": 0,
        "Count of Images": getImageCount(files),
        "Count of Videos": getVideoCount(files),
        "Count of Gifs": messageInput.gifList.length,
        "Count of Files": messageInput.fileList?.length ?? 0,
        "Count of Quoted Messages": messageInput.quotedMessageList?.length ?? 0,
        Source: "App",
        "SMS Outbound": hasSmsUsers(messageThread),
        "SMS Received Count": smsUserCount(messageThread),
      });
      incrementPersonProperty(PERSON_COUNT_TYPE.MESSAGES_SENT);
      if (messageThreadInput.isLocked) {
        track(MIXPANEL_EVENT_TYPE.LOCK_CONVERSATION_TOGGLED, {
          "Conversation Locked": true,
          "New Conversation": true,
        });
      }
      if ((messageThreadInput.createMessageUserIdList?.length ?? 0) > 0) {
        track(MIXPANEL_EVENT_TYPE.READ_ONLY_CONVERSATION_TOGGLED, {
          "Read-Only Set": true,
          "New Conversation": true,
        });
      }

      return messageThread;
    },
    [
      createMessageThreadAPI,
      getImageCount,
      getVideoCount,
      hasSmsUsers,
      incrementPersonProperty,
      smsUserCount,
      track,
    ],
  );

  const [createMessageAPI, { loading: isCreateMessageLoading }] = useMutation<
    CreateMessageMutation,
    CreateMessageMutationVariables
  >(CREATE_MESSAGE_MUTATION);

  interface ICreateMessageParams {
    messageInput: MessageInput;
    files?: IFileInTransit[];
    quotedMessages?: QuotedMessageFragment[];
    isQuickReply?: boolean;
  }

  const createMessage = useCallback(
    async ({
      messageInput,
      files,
      quotedMessages,
      isQuickReply = false,
    }: ICreateMessageParams) => {
      if (!me) throw new Error("Current user not found");
      if (!messageInput) throw new Error("Message Input not found");
      if (!messageThreadId) {
        throw new Error("message thread not found");
      }
      const optimisticMessage = getOptimisticMessage(
        messageThreadId,
        messageInput,
        files,
        quotedMessages,
      );
      if (!optimisticMessage) throw new Error("Optimistic message not found");
      publish("draft-message-sent", optimisticMessage);
      await createMessageAPI({
        variables: {
          messageThreadId,
          input: messageInput,
        },
        onError: (error) => {
          //TODO: What do we do when this occurs?
          throw new Error(error.message);
        },
        onCompleted(data) {
          if (!data?.createMessage) {
            return;
          }
          publish("new-message", data?.createMessage);
        },
      });

      const calloutUsers = messageInput.calloutUserIdList.filter(
        (userId) => !isEmailUser(userId),
      );
      const emailUsers = messageInput.calloutUserIdList.filter(isEmailUser);

      const imageCount =
        files?.filter((obj) => isImage(obj.contentType)).length ?? 0;
      const videoCount =
        files?.filter((obj) => isVideo(obj.contentType)).length ?? 0;

      const messageThread = cache.readFragment<MessageThreadFragment>({
        id: `MessageThread:${messageThreadId}`,
        fragment: MESSAGE_THREAD_FRAGMENT,
        fragmentName: "MessageThread",
      });

      if (!messageThread) return;

      const mixpanelProperties = {
        "New Conversation": false,
        "Conversation Type": getMessageThreadTypeName(messageThread),
        "Linked Conversation": false,
        "Count of Hashtags": messageInput.tagIdList.length,
        "Count of Dist Groups": messageInput.distributionGroupIdList.length,
        "Count of Callouts": calloutUsers.length,
        "Count of Emails": emailUsers.length,
        "Count of Photos": 0,
        "Count of Images": imageCount,
        "Count of Videos": videoCount,
        "Count of Gifs": messageInput.gifList.length,
        "Count of Files": messageInput.fileList?.length ?? 0,
        "Inline Reply": !!quotedMessages?.length,
        "Count of Quoted Messages": quotedMessages?.length ?? 0,
        Source: "App",
        "SMS Outbound": hasSmsUsers(messageThread),
        "SMS Received Count": smsUserCount(messageThread),
        ...(isQuickReply && { "Quick Reply": messageInput.text }),
      };

      //TODO: if the quoted messages are from a different thread, then track the thread type as the Conversation Source

      track(MIXPANEL_EVENT_TYPE.MESSAGE_SENT, mixpanelProperties);

      incrementPersonProperty(PERSON_COUNT_TYPE.MESSAGES_SENT);
    },
    [
      cache,
      createMessageAPI,
      getOptimisticMessage,
      hasSmsUsers,
      incrementPersonProperty,
      isEmailUser,
      me,
      messageThreadId,
      publish,
      smsUserCount,
      track,
    ],
  );

  const getQuotedMessage = useCallback((message: MessageFragment) => {
    const quotedMessage: QuotedMessageFragment = {
      __typename: "QuotedMessage",
      id: message.id,
      messageId: message.id,
      messageThreadId: message.messageThreadId,
      parentMessageThreadId: "",
      parentMessageId: "",
      createdUserId: message.createdUserId,
      createTime: message.createTime,
      text: message.text,
      quillDelta: message.quillDelta,
      localizedText: message.localizedText,
      locale: message.locale,
      canAccessMessageThread: true,
    };
    return quotedMessage;
  }, []);

  const isOnThreadPath = useMemo(
    () => pathname.includes(THREADS_PATH),
    [pathname],
  );

  const isOnChatsPath = useMemo(
    () => pathname.includes(CHATS_PATH),
    [pathname],
  );

  const IsOnHomePath = useMemo(() => pathname.includes(HOME_PATH), [pathname]);

  const navigateToNewThread = useCallback(
    (messageThread: MessageThreadFragment) => {
      const newQuery = { ...query };
      delete newQuery[MESSAGE_THREAD_ID_QUERY_STRING];
      newQuery[MESSAGE_THREAD_ID_QUERY_STRING] = messageThread.id;
      switch (messageThread.type) {
        case MessageThreadType.Thread: {
          if (isOnThreadPath || IsOnHomePath) {
            push({
              query: newQuery,
            });
            return;
          }
          push({
            pathname: THREADS_PATH,
            query: newQuery,
          });
          break;
        }
        case MessageThreadType.Chat: {
          if (isOnChatsPath || IsOnHomePath) {
            push({
              query: newQuery,
            });
            return;
          }
          push({
            pathname: CHATS_PATH,
            query: newQuery,
          });
          break;
        }
        case MessageThreadType.Task: {
          push({
            query: newQuery,
          });
          break;
        }
        default:
          break;
      }
    },
    [IsOnHomePath, isOnChatsPath, isOnThreadPath, push, query],
  );

  return {
    createMessageThread,
    createMessage,
    isSending: isCreateMessageThreadLoading || isCreateMessageLoading,
    getQuotedMessage,
    getQuickReplyMessageInput,
    getMessageInputFromDraft,
    getMessageThreadInputFromDraft,
    navigateToNewThread,
  };
};

export default useCreateMessage;
